/**
 * Fashion Catalog Theme - Alpine.js Notification System
 *
 * Global notification manager with:
 * - Auto-dismiss with progress bar
 * - Pause on hover
 * - Stacking support
 * - Queue management
 * - Smooth enter/leave transitions
 * - Dark/light mode support
 *
 * Usage:
 * - Server-side: PHP renders initial notifications in notices.inc.php
 * - Client-side: window.Notification.success('Message') / .error() / .warning() / .info()
 */

export default () => ({
  // Notification queue (array of notification objects)
  notifications: [],

  // Maximum visible notifications at once
  maxVisible: 5,

  // Default auto-dismiss timeout (ms)
  defaultDuration: 5000,

  // Unique ID counter for notifications
  nextId: 1,

  /**
   * Initialize the notification system
   */
  init() {
    // Process any server-rendered notifications (from PHP)
    this.processServerNotifications();

    // Set up global API for programmatic notifications
    this.setupGlobalAPI();

    // Listen for custom notification events
    window.addEventListener('show-notification', e => {
      const { message, type, duration } = e.detail;
      this.add(message, type, duration);
    });
  },

  /**
   * Process server-rendered notifications from PHP
   * Converts static PHP-rendered notices into Alpine.js reactive notifications
   */
  processServerNotifications() {
    const serverNotices = document.querySelectorAll('[data-server-notice]');

    serverNotices.forEach(notice => {
      const message = notice.dataset.message;
      const type = notice.dataset.type;

      if (message) {
        this.add(message, type, this.defaultDuration, false);
      }

      // Remove the server-rendered element
      notice.remove();
    });
  },

  /**
   * Set up global window.Notification API
   * Replace the fallback API with the real Alpine-powered notification system
   */
  setupGlobalAPI() {
    const self = this;

    // Save reference to fallback queue and state
    const fallbackQueue = window.NotificationState ? window.NotificationState.queue : [];

    // Replace with real notification methods BEFORE marking initialized
    // (prevents window between initialized=true and replacement where calls are dropped)
    window.Notification = {
      success: (message, duration) => {
        return self.add(message, 'success', duration);
      },
      error: (message, duration) => {
        return self.add(message, 'error', duration);
      },
      warning: (message, duration) => {
        return self.add(message, 'warning', duration);
      },
      info: (message, duration) => {
        return self.add(message, 'info', duration);
      },
      // Alias for notices
      notice: (message, duration) => {
        return self.add(message, 'info', duration);
      },
      // Expose internal state for debugging
      _state: window.NotificationState || { initialized: true, queue: [] },
    };

    // Mark as initialized AFTER replacement (so fallback never drops calls)
    if (window.NotificationState) {
      window.NotificationState.initialized = true;
    }

    // Process any queued notifications from the fallback
    if (fallbackQueue && fallbackQueue.length > 0) {
      fallbackQueue.forEach(({ message, type, duration }) => {
        self.add(message, type, duration);
      });

      // Clear the queue
      if (window.NotificationState) {
        window.NotificationState.queue = [];
      }
    }
  },

  /**
   * Add a new notification
   *
   * @param {string} message - The notification message (can contain HTML)
   * @param {string} type - Type: 'success', 'error', 'warning', 'info'
   * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @param {boolean} prepend - Add to start of queue (true) or end (false)
   * @returns {object} The created notification object
   */
  add(message, type = 'info', duration = null, prepend = true) {
    const notification = {
      id: this.nextId++,
      message,
      type,
      duration: duration !== null ? duration : this.defaultDuration,
      progress: 100,
      paused: false,
      progressInterval: null,
      timeoutId: null,
      startTime: Date.now(),
      remainingTime: duration !== null ? duration : this.defaultDuration,
      visible: true,
    };

    // Add to queue (prepend for new notifications, append for server-rendered)
    if (prepend) {
      this.notifications.unshift(notification);
    } else {
      this.notifications.push(notification);
    }

    // Enforce max visible limit
    if (this.notifications.length > this.maxVisible) {
      const removed = this.notifications.splice(this.maxVisible);
      removed.forEach(n => this.clearTimers(n));
    }

    // Start auto-dismiss timer if duration > 0
    if (notification.duration > 0) {
      this.startAutoDismiss(notification);
    }

    return notification;
  },

  /**
   * Remove a notification by ID
   */
  remove(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (!notification) return;

    // Clear timers
    this.clearTimers(notification);

    // Fade out animation
    notification.visible = false;

    // Remove from array after animation completes
    setTimeout(() => {
      const index = this.notifications.findIndex(n => n.id === id);
      if (index !== -1) {
        this.notifications.splice(index, 1);
      }
    }, 300); // Match transition duration
  },

  /**
   * Start auto-dismiss timer and progress bar animation
   */
  startAutoDismiss(notification) {
    const updateInterval = 50; // Update progress every 50ms
    const steps = notification.remainingTime / updateInterval;
    const progressDecrement = 100 / steps;

    notification.startTime = Date.now();

    notification.progressInterval = setInterval(() => {
      if (!notification.paused) {
        notification.progress -= progressDecrement;

        if (notification.progress <= 0) {
          this.remove(notification.id);
        }
      }
    }, updateInterval);

    // Fallback timeout (in case progress interval fails)
    notification.timeoutId = setTimeout(() => {
      if (!notification.paused) {
        this.remove(notification.id);
      }
    }, notification.remainingTime);
  },

  /**
   * Pause auto-dismiss on hover
   */
  pause(notification) {
    if (notification.paused || notification.duration === 0) return;

    notification.paused = true;

    // Calculate remaining time
    const elapsed = Date.now() - notification.startTime;
    notification.remainingTime = Math.max(0, notification.duration - elapsed);
  },

  /**
   * Resume auto-dismiss
   */
  resume(notification) {
    if (!notification.paused || notification.duration === 0) return;

    notification.paused = false;

    // Clear existing timers
    this.clearTimers(notification);

    // Restart with remaining time
    notification.duration = notification.remainingTime;
    this.startAutoDismiss(notification);
  },

  /**
   * Clear all timers for a notification
   */
  clearTimers(notification) {
    if (notification.progressInterval) {
      clearInterval(notification.progressInterval);
      notification.progressInterval = null;
    }
    if (notification.timeoutId) {
      clearTimeout(notification.timeoutId);
      notification.timeoutId = null;
    }
  },

  /**
   * Get notification config (icon, colors) by type
   * Editorial fashion theme style with accent bar
   */
  getConfig(type) {
    const configs = {
      success: {
        icon: 'check',
        label: window.tx('notifSuccess', 'Success'),
        cardClass: 'bg-[var(--color-surface)] border border-[var(--color-border)] shadow-lg',
        accentBar: 'bg-emerald-500',
        iconBg: 'bg-emerald-500/10',
        iconColor: 'text-emerald-600',
        labelColor: 'text-emerald-600',
        progressBar: 'bg-emerald-500',
      },
      error: {
        icon: 'x',
        label: window.tx('notifError', 'Error'),
        cardClass: 'bg-[var(--color-surface)] border border-[var(--color-border)] shadow-lg',
        accentBar: 'bg-red-500',
        iconBg: 'bg-red-500/10',
        iconColor: 'text-red-600',
        labelColor: 'text-red-600',
        progressBar: 'bg-red-500',
      },
      warning: {
        icon: 'alert-triangle',
        label: window.tx('notifWarning', 'Warning'),
        cardClass: 'bg-[var(--color-surface)] border border-[var(--color-border)] shadow-lg',
        accentBar: 'bg-amber-500',
        iconBg: 'bg-amber-500/10',
        iconColor: 'text-amber-600',
        labelColor: 'text-amber-600',
        progressBar: 'bg-amber-500',
      },
      info: {
        icon: 'info',
        label: window.tx('notifInfo', 'Info'),
        cardClass: 'bg-[var(--color-surface)] border border-[var(--color-border)] shadow-lg',
        accentBar: 'bg-[var(--color-accent)]',
        iconBg: 'bg-[var(--color-accent)]/10',
        iconColor: 'text-[var(--color-accent)]',
        labelColor: 'text-[var(--color-accent)]',
        progressBar: 'bg-[var(--color-accent)]',
      },
    };

    return configs[type] || configs.info;
  },

  /**
   * Clear all notifications
   */
  clearAll() {
    // Clear all timers
    this.notifications.forEach(n => this.clearTimers(n));

    // Fade out all
    this.notifications.forEach(n => (n.visible = false));

    // Clear array after animation
    setTimeout(() => {
      this.notifications = [];
    }, 300);
  },
});
